<template>
  <Teleport to="body">
    <Transition name="public-theme-fab">
      <button
        v-if="visible"
        type="button"
        class="public-theme-fab"
        :class="{ 'public-theme-fab--elevated': hasBottomFab }"
        :style="fabStyle"
        :aria-label="t('app.themeTab')"
        data-testid="public-theme-fab"
        @click="openModal"
      >
        <span class="material-icons public-theme-fab__icon" aria-hidden="true">palette</span>
        <span class="public-theme-fab__label" aria-hidden="true">{{ t('app.themeTab') }}</span>
      </button>
    </Transition>

    <!-- Modal -->
    <Transition name="public-theme-modal">
      <div
        v-if="modelValue"
        class="public-theme-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="public-theme-modal-title"
        aria-describedby="public-theme-modal-desc"
        data-testid="public-theme-modal"
      >
        <div
          class="public-theme-modal__backdrop"
          aria-hidden="true"
          @click="closeModal"
        />
        <div class="public-theme-modal__card" @click.stop>
          <div class="public-theme-modal__header">
            <h2 id="public-theme-modal-title" class="public-theme-modal__title">
              <span class="material-icons public-theme-modal__icon" aria-hidden="true">palette</span>
              {{ t('app.themeTab') }}
            </h2>
            <button
              type="button"
              class="public-theme-modal__close"
              :aria-label="t('public.close')"
              @click="closeModal"
            >
              <span class="material-icons" aria-hidden="true">close</span>
            </button>
          </div>
          <div id="public-theme-modal-desc" class="public-theme-modal__body">
            <!-- Primary color -->
            <section class="public-theme-modal__section">
              <h3 class="public-theme-modal__section-title">{{ t('app.primaryColorLabel') }}</h3>
              <p class="public-theme-modal__hint">{{ t('app.primaryColorHint') }}</p>
              <div class="public-theme-modal__color-row">
                <input
                  :value="localPrimaryColor || '#000000'"
                  type="color"
                  class="public-theme-modal__color-picker"
                  :aria-label="t('app.ariaPickPrimaryColor')"
                  data-testid="public-theme-primary-color-picker"
                  @input="(e) => { localPrimaryColor = e.target?.value ?? ''; onColorInput() }"
                >
                <div class="public-theme-modal__hex-wrap">
                  <input
                    id="public-theme-hex"
                    v-model="localPrimaryColor"
                    type="text"
                    maxlength="9"
                    placeholder="#e85a2b"
                    class="public-theme-modal__hex-input"
                    data-testid="public-theme-primary-color-hex"
                    @input="onHexInput"
                  >
                </div>
                <button
                  type="button"
                  class="public-theme-modal__clear-btn"
                  :disabled="!localPrimaryColor"
                  data-testid="public-theme-primary-color-clear"
                  @click="localPrimaryColor = ''; onColorInput()"
                >
                  {{ t('app.clear') }}
                </button>
              </div>
              <p v-if="primaryColorError" class="public-theme-modal__error" role="alert">{{ primaryColorError }}</p>
            </section>

            <!-- Template -->
            <section class="public-theme-modal__section">
              <h3 class="public-theme-modal__section-title">{{ t('app.wizardTemplateLabel') }}</h3>
              <p class="public-theme-modal__hint">{{ t('app.themeTemplateHint') }}</p>
              <div class="public-theme-modal__template-grid" role="group" :aria-label="t('app.ariaSelectTemplate')">
                <button
                  v-for="opt in filteredTemplateOptions"
                  :key="opt.id"
                  type="button"
                  :data-testid="`public-theme-template-${opt.id}`"
                  class="public-theme-modal__template-card"
                  :class="{ 'public-theme-modal__template-card--selected': localTemplate === opt.id }"
                  :aria-pressed="localTemplate === opt.id"
                  :style="localTemplate === opt.id ? { '--card-accent': effectiveColor } : undefined"
                  @click="localTemplate = opt.id"
                >
                  <span class="public-theme-modal__template-check" :class="{ 'public-theme-modal__template-check--on': localTemplate === opt.id }">
                    <span v-if="localTemplate === opt.id" class="material-icons text-sm" aria-hidden="true">check</span>
                  </span>
                  <div class="public-theme-modal__template-preview">
                    <img
                      v-if="templatePreviewUrl(opt.id) && !previewLoadFailed[opt.id]"
                      :src="templatePreviewUrl(opt.id)"
                      :alt="opt.name"
                      loading="lazy"
                      @error="previewLoadFailed[opt.id] = true"
                    >
                    <span v-else class="material-icons public-theme-modal__template-placeholder">dashboard</span>
                  </div>
                  <span class="public-theme-modal__template-name">{{ opt.name }}</span>
                  <span class="public-theme-modal__template-desc">{{ opt.description }}</span>
                </button>
              </div>
              <p v-if="templateError" class="public-theme-modal__error" role="alert">{{ templateError }}</p>
            </section>
          </div>
          <div class="public-theme-modal__footer">
            <button
              type="button"
              class="public-theme-modal__cancel"
              data-testid="public-theme-modal-cancel"
              @click="closeModal"
            >
              {{ t('app.cancel') }}
            </button>
            <button
              type="button"
              class="public-theme-modal__save"
              :style="{ backgroundColor: effectiveColor }"
              :disabled="saving || !canSave"
              data-testid="public-theme-modal-save"
              @click="save"
            >
              {{ saving ? t('app.saving') : t('app.save') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ALLOWED_TEMPLATES,
  getTemplateOptionsForBusinessType,
  getAllowedTemplateIdsForBusinessType,
  getTemplatePreviewUrl as getTemplatePreviewUrlConst,
} from '@/constants/templates'

const PRIMARY_COLOR_REGEX = /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3}([0-9A-Fa-f]{2})?)?$/
function isValidPrimaryColor(hex) {
  if (!hex || typeof hex !== 'string') return false
  return hex.trim() === '' || PRIMARY_COLOR_REGEX.test(hex.trim())
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  restaurant: { type: Object, default: null },
  /** When true, FAB is positioned higher to avoid overlapping another FAB (e.g. Quick Order). */
  hasBottomFab: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'preview-color', 'save', 'close'])

const { t } = useI18n()

const templatePreviewBaseUrl = typeof import.meta.env.BASE_URL === 'string' ? import.meta.env.BASE_URL.replace(/\/$/, '') : ''
function templatePreviewUrl(templateId) {
  return getTemplatePreviewUrlConst(templatePreviewBaseUrl, templateId)
}

const visible = computed(() => !!props.restaurant?.viewer?.is_owner)
const currentBusinessType = computed(() => props.restaurant?.business_type || 'restaurants')
const filteredTemplateOptions = computed(() => getTemplateOptionsForBusinessType(currentBusinessType.value))

/** Color for FAB and Save button; when modal is open use local value for preview. */
const effectiveColor = computed(() => {
  if (props.modelValue && localPrimaryColor.value && isValidPrimaryColor(localPrimaryColor.value)) {
    return localPrimaryColor.value.trim()
  }
  const c = (props.restaurant?.primary_color ?? '').trim()
  return c || '#2563eb'
})

const fabStyle = computed(() => ({
  backgroundColor: effectiveColor.value,
}))

const localPrimaryColor = ref('')
const localTemplate = ref('template-1')
const primaryColorError = ref('')
const templateError = ref('')
const saving = ref(false)
const previewLoadFailed = ref({})

watch(
  () => props.modelValue,
  (open) => {
    if (open && props.restaurant) {
      localPrimaryColor.value = (props.restaurant.primary_color ?? '').trim()
      const allowed = getAllowedTemplateIdsForBusinessType(currentBusinessType.value)
      localTemplate.value = ALLOWED_TEMPLATES.includes(props.restaurant.template) && allowed.includes(props.restaurant.template)
        ? props.restaurant.template
        : (allowed[0] || 'template-1')
      primaryColorError.value = ''
      templateError.value = ''
    }
  },
  { immediate: true }
)

watch(
  () => [props.restaurant?.primary_color, props.restaurant?.template],
  ([pc, tmpl]) => {
    if (props.restaurant && !props.modelValue) return
    if (pc !== undefined) localPrimaryColor.value = (pc ?? '').trim()
    if (tmpl !== undefined) {
      const allowed = getAllowedTemplateIdsForBusinessType(currentBusinessType.value)
      localTemplate.value = ALLOWED_TEMPLATES.includes(tmpl) && allowed.includes(tmpl)
        ? tmpl
        : (allowed[0] || 'template-1')
    }
  }
)

/** Emit preview whenever local color changes so the site reflects it immediately (without saving). */
watch(
  () => localPrimaryColor.value,
  (v) => {
    if (!props.modelValue) return
    const trimmed = (v ?? '').trim()
    primaryColorError.value = trimmed && !isValidPrimaryColor(trimmed) ? t('app.primaryColorInvalid') : ''
    emit('preview-color', trimmed || null)
  }
)

const canSave = computed(() => {
  const hexOk = !localPrimaryColor.value || isValidPrimaryColor(localPrimaryColor.value)
  const templateOk = ALLOWED_TEMPLATES.includes(localTemplate.value)
    && getAllowedTemplateIdsForBusinessType(currentBusinessType.value).includes(localTemplate.value)
  const colorChanged = (localPrimaryColor.value || null) !== (props.restaurant?.primary_color ?? null)
  const templateChanged = localTemplate.value !== (props.restaurant?.template ?? 'template-1')
  return hexOk && templateOk && (colorChanged || templateChanged)
})

function onColorInput() {
  const v = (localPrimaryColor.value ?? '').trim()
  primaryColorError.value = v && !isValidPrimaryColor(v) ? t('app.primaryColorInvalid') : ''
  emit('preview-color', v || null)
}

function onHexInput(e) {
  const v = (e.target?.value ?? '').trim()
  localPrimaryColor.value = v
  primaryColorError.value = v && !isValidPrimaryColor(v) ? t('app.primaryColorInvalid') : ''
  emit('preview-color', v || null)
}

function openModal() {
  emit('update:modelValue', true)
  if (props.restaurant) {
    localPrimaryColor.value = (props.restaurant.primary_color ?? '').trim()
    const allowed = getAllowedTemplateIdsForBusinessType(currentBusinessType.value)
    localTemplate.value = ALLOWED_TEMPLATES.includes(props.restaurant.template) && allowed.includes(props.restaurant.template)
      ? props.restaurant.template
      : (allowed[0] || 'template-1')
  }
}

function closeModal() {
  emit('update:modelValue', false)
  emit('close')
}

async function save() {
  if (saving.value || !canSave.value || !props.restaurant?.uuid) return
  if (!isValidPrimaryColor(localPrimaryColor.value) && localPrimaryColor.value) {
    primaryColorError.value = t('app.primaryColorInvalid')
    return
  }
  saving.value = true
  primaryColorError.value = ''
  templateError.value = ''
  try {
    emit('save', {
      primary_color: (localPrimaryColor.value ?? '').trim() || null,
      template: localTemplate.value,
    })
    emit('update:modelValue', false)
    emit('close')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* Always on top of owner sticky and view menu bar: use high z-index so FAB wins regardless of app stacking context */
.public-theme-fab {
  position: fixed;
  bottom: 8rem;
  right: 1rem;
  z-index: 9998;
  min-height: 48px;
  padding: 0 1rem;
  border-radius: 9999px;
  border: none;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s, opacity 0.15s;
}
/* 300px–400px: sit well above view menu bar + owner bar */
@media (max-width: 420px) {
  .public-theme-fab {
    bottom: 10rem;
    right: 0.75rem;
  }
}
/* 315px and below: extra bottom so FAB is never covered by sticky bars */
@media (max-width: 315px) {
  .public-theme-fab {
    bottom: 12rem;
  }
}
/* 401px–767px: standard mobile clearance */
@media (min-width: 421px) and (max-width: 767px) {
  .public-theme-fab {
    bottom: 8rem;
    right: 1rem;
  }
}
/* 768px onwards: desktop – above owner sticky only */
@media (min-width: 768px) {
  .public-theme-fab {
    bottom: 5rem;
    right: 1rem;
  }
}
.public-theme-fab--elevated {
  bottom: 12rem;
}
.public-theme-fab:hover {
  transform: scale(1.02);
}
.public-theme-fab:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.public-theme-fab__icon {
  font-size: 1.25rem;
}
.public-theme-fab__label {
  font-size: 0.875rem;
  font-weight: 600;
}

.public-theme-modal {
  position: fixed;
  inset: 0;
  z-index: 10003;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}
@media (min-width: 640px) {
  .public-theme-modal {
    align-items: center;
    padding: 1rem;
  }
}
.public-theme-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}
.public-theme-modal__card {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  background: var(--rms-bg, #fff);
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (min-width: 640px) {
  .public-theme-modal__card {
    border-radius: 1rem;
    max-height: 85vh;
  }
}
.public-theme-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--rms-border, #e2e8f0);
}
.public-theme-modal__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.public-theme-modal__icon {
  font-size: 1.5rem;
  opacity: 0.9;
}
.public-theme-modal__close {
  padding: 0.5rem;
  border: none;
  background: transparent;
  border-radius: 0.5rem;
  color: inherit;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.public-theme-modal__close:hover {
  background: rgba(0, 0, 0, 0.06);
}
.public-theme-modal__body {
  overflow-y: auto;
  padding: 1rem 1.25rem;
  flex: 1;
  min-height: 0;
}
.public-theme-modal__section {
  margin-bottom: 1.5rem;
}
.public-theme-modal__section:last-child {
  margin-bottom: 0;
}
.public-theme-modal__section-title {
  margin: 0 0 0.25rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--rms-muted, #64748b);
}
.public-theme-modal__hint {
  margin: 0 0 0.75rem 0;
  font-size: 0.875rem;
  color: var(--rms-muted, #64748b);
}
.public-theme-modal__color-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}
.public-theme-modal__color-picker {
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  border-radius: 0;
  border: 1px solid var(--rms-border, #e2e8f0);
  cursor: pointer;
  padding: 0;
}
.public-theme-modal__hex-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.public-theme-modal__hex-label {
  font-size: 0.875rem;
  font-weight: 500;
}
.public-theme-modal__hex-input {
  width: 8rem;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--rms-border, #e2e8f0);
  font-family: ui-monospace, monospace;
  font-size: 0.875rem;
}
.public-theme-modal__clear-btn {
  min-height: 44px;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--rms-border, #e2e8f0);
  background: var(--rms-bg-secondary, #f8fafc);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}
.public-theme-modal__clear-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.public-theme-modal__error {
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  color: #dc2626;
}
.public-theme-modal__template-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}
.public-theme-modal__template-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 2px solid var(--rms-border, #e2e8f0);
  background: var(--rms-bg-secondary, #f8fafc);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  min-height: 44px;
}
.public-theme-modal__template-card:hover {
  border-color: var(--rms-border-hover, #cbd5e1);
}
.public-theme-modal__template-card--selected {
  border-color: var(--card-accent, #2563eb);
  background: color-mix(in srgb, var(--card-accent, #2563eb) 8%, transparent);
}
.public-theme-modal__template-check {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: var(--rms-border, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #fff;
}
.public-theme-modal__template-check--on {
  background: var(--card-accent, #2563eb);
}
.public-theme-modal__template-preview {
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 0.5rem;
  background: var(--rms-border, #e2e8f0);
  margin-bottom: 0.5rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.public-theme-modal__template-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}
.public-theme-modal__template-placeholder {
  font-size: 1.5rem;
  opacity: 0.5;
}
.public-theme-modal__template-name {
  font-size: 0.875rem;
  font-weight: 600;
}
.public-theme-modal__template-desc {
  font-size: 0.75rem;
  color: var(--rms-muted, #64748b);
  margin-top: 0.25rem;
  line-height: 1.3;
}
.public-theme-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--rms-border, #e2e8f0);
}
.public-theme-modal__cancel {
  min-height: 44px;
  padding: 0 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--rms-border, #e2e8f0);
  background: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}
.public-theme-modal__save {
  min-height: 44px;
  padding: 0 1.25rem;
  border-radius: 0.5rem;
  border: none;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}
.public-theme-modal__save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Transitions */
.public-theme-fab-enter-active,
.public-theme-fab-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.public-theme-fab-enter-from,
.public-theme-fab-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
.public-theme-modal-enter-active,
.public-theme-modal-leave-active {
  transition: opacity 0.2s;
}
.public-theme-modal-enter-active .public-theme-modal__card,
.public-theme-modal-leave-active .public-theme-modal__card {
  transition: transform 0.2s;
}
.public-theme-modal-enter-from .public-theme-modal__card,
.public-theme-modal-leave-to .public-theme-modal__card {
  transform: translateY(100%);
}
@media (min-width: 640px) {
  .public-theme-modal-enter-from .public-theme-modal__card,
  .public-theme-modal-leave-to .public-theme-modal__card {
    transform: scale(0.95);
  }
}
</style>
